import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, GraduationCap, Mic, FileSignature, Scale } from "lucide-react";
import { Seo } from "@/components/Seo";

const positions = [
  {
    icon: GraduationCap,
    title: "Coordination du dispositif « Exprime la Charte »",
    subtitle: "Bénévole expert·e Éducation nationale — déploiement national d'un parcours civique pour collégiens et lycéens",
    mission:
      "« Exprime la Charte » invite les classes de la 4e à la 1re à s'approprier les valeurs républicaines par une production créative (texte, image, vidéo, son). Après une première expérimentation menée avec l'Académie d'Aix-Marseille, nous changeons d'échelle pour la rentrée 2026/2027 en repositionnant le dispositif comme un parcours pédagogique court (atelier en classe, puis concours national facultatif). Nous recherchons une équipe de bénévoles connaissant de l'intérieur le fonctionnement de l'Éducation nationale pour piloter ce déploiement aux côtés d'un membre.",
    activities: [
      "Mobiliser les relais académiques : rectorats, référents EMC, référents culture, chefs d'établissement, et identifier des établissements pilotes.",
      "Adapter et simplifier le dispositif et ses supports pour qu'ils s'intègrent sans surcharge dans le temps de classe.",
      "Accompagner les enseignants engagés (prise en main, points d'étape, valorisation des productions).",
      "Contribuer à l'organisation du jury et de la cérémonie de remise des prix.",
    ],
    profile:
      "Carrière dans l'Éducation nationale (enseignant·e, professeur·e, CPE, personnel d'inspection ou de direction). Bonne connaissance des circuits académiques et du calendrier scolaire. Aisance relationnelle, sens de l'organisation, goût pour la transmission. Disponibilité régulière, compatible avec une retraite active.",
    modalities:
      "Bénévolat, télétravail possible avec quelques déplacements ponctuels. Rythme à convenir selon vos disponibilités. Engagement souhaité sur l'année scolaire 2026-2027.",
    subject: "Bénévolat Exprime la Charte",
  },
  {
    icon: Mic,
    title: "Coordination de la Soirée « À Haute Voix »",
    subtitle: "Bénévole expert·e événementiel ou journaliste — production d'une soirée civique au Théâtre de la Tour Eiffel",
    mission:
      "Le mercredi 25 novembre 2026, EDLR organise « À Haute Voix — Promouvoir notre socle civique » au Théâtre de la Tour Eiffel (Paris 7e) : deux heures mêlant prise de parole, lecture portée et musique, devant 400 personnes en salle et en diffusion nationale (web). Nous recherchons un·e bénévole issu·e de l'événementiel ou du journalisme pour structurer la production et la couverture éditoriale de la soirée.",
    activities: [
      "Profil événementiel : conducteur minute par minute, coordination de la régie, des intervenants et des prestataires techniques (son, lumière, captation, streaming), répétition générale et déroulé le soir J.",
      "Profil journaliste / éditorial : préparation des séquences de parole et des transitions, dossier de presse, relations avec les partenaires médias, supervision de la captation et déclinaison des contenus après l'événement (clips, citations, formats éditoriaux).",
    ],
    profile:
      "Expérience confirmée de la production d'événements (culturels, institutionnels, scéniques) ou du journalisme / de la communication éditoriale. Rigueur, sang-froid le jour J, sens du tempo. Adhésion au caractère strictement non partisan de la démarche. Disponibilité renforcée en octobre-novembre 2026, compatible avec une retraite active.",
    modalities: "Bénévolat, avec présence requise lors des répétitions et le soir de l'événement à Paris.",
    subject: "Bénévolat Soirée À Haute Voix",
  },
  {
    icon: FileSignature,
    title: "Coordination de la campagne de signatures de la Charte",
    subtitle: "Bénévole coordinateur·rice — campagne nationale de signature de la Charte par les citoyens et les élus",
    mission:
      "La Charte est aujourd'hui signée par tout nouveau citoyen lors de sa naturalisation, mais reste méconnue des Français de naissance. EDLR en fait un engagement citoyen vivant : « Signez, vous comptez ; faites-la signer, vous changez les choses. » Nous voulons lancer une campagne nationale visant deux publics — le grand public et, surtout, les élus locaux et nationaux — et recherchons une équipe de bénévoles pour l'animer et la piloter.",
    activities: [
      "Construire et animer le volet « élus » : identifier et contacter mairies, conseils départementaux et régionaux, parlementaires ; organiser les sollicitations et le suivi des signatures.",
      "Coordonner le volet grand public : mobilisation des sympathisants, mise en avant du compteur de signataires, temps forts de signature dans des lieux emblématiques.",
      "Tenir le tableau de bord des signatures et en assurer le reporting auprès de la Présidence.",
      "Articuler la campagne avec les autres actions de l'association (notamment la soirée du 25 novembre 2026, où la signature sera relayée en direct).",
    ],
    profile:
      "Sens de l'organisation et de la relation institutionnelle, aisance à démarcher et à fédérer. Une expérience politique locale (mandat, travail dans une collectivité territoriale) ou une bonne connaissance des circuits des élus sont les bienvenues. Adhésion sans réserve au caractère non partisan de la démarche. Disponibilité régulière, compatible avec une retraite active.",
    modalities:
      "Bénévolat, télétravail possible avec déplacements ponctuels (rendez-vous d'élus, temps forts). Rythme à convenir. Engagement souhaité sur l'année 2026 et 2027.",
    subject: "Bénévolat Campagne Charte",
  },
  {
    icon: Scale,
    title: "Construction de l'Observatoire EDLR",
    subtitle: "Bénévole — construction de l'Observatoire de la citoyenneté (profil droit public / constitutionnel recommandé)",
    mission:
      "EDLR lance l'Observatoire Enfants de la République : non un simple centre d'études, mais une grille de lecture rigoureuse et sourcée à l'heure de la désinformation et de la polarisation. Il portera l'Indice de Citoyenneté Française et son baromètre annuel, produira des « Notes de l'Observatoire » et conduira un dispositif inédit : l'analyse de la compatibilité des programmes des candidats à la présidentielle 2027 avec les principes républicains. Nous recherchons un·e bénévole juriste pour participer à bâtir la colonne vertébrale méthodologique et juridique et surtout l'animation de ce dispositif.",
    activities: [
      "Participer à la méthodologie d'analyse et mise en pratique pour audit des programmes présidentiels 2027 sous l'angle de la compatibilité aux principes que la Charte rappelle.",
      "Contribuer à la rédaction et à la validation juridique des « Notes de l'Observatoire » et appuyer la constitution du collège d'experts pluridisciplinaire.",
      "Sécuriser la qualité juridique des prises de parole publiques de l'Observatoire (factualité, exactitude des fondements).",
    ],
    profile:
      "Formation et expérience en droit ; une spécialisation en droit constitutionnel ou droit public est particulièrement bienvenue (magistrature, barreau, université, fonction publique, conseil). Goût de la rigueur documentaire et du sourcing, sens de la synthèse. Adhésion sans réserve à la neutralité de la démarche. Disponibilité régulière, compatible avec une retraite active.",
    modalities:
      "Bénévolat, télétravail très majoritaire, quelques réunions du collège d'experts. Démarrage de la structuration à l'automne 2026, dans le prolongement de la soirée du 25 novembre. Rythme à convenir.",
    subject: "Bénévolat Observatoire",
  },
];

export default function Benevolat() {
  return (
    <div className="min-h-screen">
      <Seo title="Devenir bénévole | Enfants de la République" description="Engagez-vous comme bénévole auprès de l'association Enfants de la République et participez à nos actions citoyennes." path="/benevolat" />
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-[#FFFD4C] via-[#FFFD4C] to-[#F5E84A] pt-28 pb-16">
        <div className="container max-w-5xl px-4">
          <div className="mb-8 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1E3A8A] mb-3">
              Devenir Bénévole
            </h1>
            <p className="text-[#1E3A8A]/90 max-w-3xl mx-auto">
              Rejoignez-nous et participez activement à la vie de l'association.
              Découvrez ci-dessous les postes ouverts et les compétences que nous recherchons.
            </p>
          </div>

          <div className="grid gap-6">
            {positions.map((p, i) => {
              const Icon = p.icon;
              const mailto = `mailto:contact@ledlr.fr?subject=${encodeURIComponent(p.subject)}`;
              return (
                <Card key={i} className="bg-white/95 border-blue-900/20 shadow-md">
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-blue-900/10 text-blue-900 shrink-0">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-blue-900">{p.title}</CardTitle>
                        <p className="text-sm text-blue-900/80 mt-1">{p.subtitle}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm text-gray-800">
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">La mission</h3>
                      <p>{p.mission}</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Vos activités</h3>
                      <ul className="list-disc pl-5 space-y-1">
                        {p.activities.map((a, j) => <li key={j}>{a}</li>)}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Profil recherché</h3>
                      <p>{p.profile}</p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Modalités</h3>
                      <p>{p.modalities}</p>
                    </div>
                    <div className="pt-2">
                      <Button asChild className="bg-blue-900 hover:bg-blue-900/90 text-white">
                        <a href={mailto}>
                          <Mail className="w-4 h-4" />
                          Candidater — {p.subject}
                        </a>
                      </Button>
                      <p className="text-xs text-gray-600 mt-2">
                        Envoyez votre candidature à contact@ledlr.fr avec pour objet « {p.subject} ».
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
